import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_SEARCH_FIELDS } from '../http-params.util';

/**
 * CouponsApiService (Angular 18) — coupons, categories, effective dates,
 * and event-template relations.
 *
 * Replaces apps/b2b-ui/common/services/api/couponsApi/couponsApi.service.js.
 *
 * Three sub-namespaces:
 *   apiService.coupons.categories.{list,detail,create,update,remove}
 *   apiService.coupons.effectiveDates.{list,detail,create,update,remove}
 *   apiService.coupons.templates.{detail,update}    update is POST (legacy quirk)
 *
 * Endpoint URLs are intentionally divergent (preserved 1:1):
 *   /v1/api/coupons                                              top-level
 *   /v1/api/coupon/categories                                    categories
 *   /v1/api/coupon/dates                                         effectiveDates
 *   /v1/api/coupon/category/event/schedule/relations/event/templates  templates
 */
const COUPONS_BASE_V1 = '/v1/api/coupons';
const COUPONS_BASE_V2 = '/v2/api/coupons';
const CATEGORIES_BASE_V1 = '/v1/api/coupon/categories';
const CATEGORIES_BASE_V2 = '/v2/api/coupon/categories';
const EFFECTIVE_DATES_BASE_V1 = '/v1/api/coupon/dates';
const TEMPLATES_BASE_V1 = '/v1/api/coupon/category/event/schedule/relations/event/templates';

const COUPONS_LIST_FIELDS = [...PAGINATION_SEARCH_FIELDS, 'startDate', 'endDate', 'redemptionStatus'] as const;

interface CouponPayload {
  id?: number | string;
  code: string;
  name: string;
  description?: string;
  programName?: string;
  companyName?: string;
  resellerId?: number | string;
  categoryId?: number | string;
  orderRedemptionLimit?: number | null;
  ticketRedemptionLimit?: number | null;
}

@Injectable({ providedIn: 'root' })
export class CouponsApiService {
  private readonly http = inject(HttpClient);

  list(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(COUPONS_BASE_V2, {
        params: buildParams(props, COUPONS_LIST_FIELDS),
      }),
    );
  }

  detail(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${COUPONS_BASE_V1}/${id}`));
  }

  create(props: CouponPayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(COUPONS_BASE_V1, {
        code: props.code,
        name: props.name,
        description: props.description,
        programName: props.programName,
        companyName: props.companyName,
        resellerId: props.resellerId,
        categoryId: props.categoryId,
        orderRedemptionLimit: props.orderRedemptionLimit,
        ticketRedemptionLimit: props.ticketRedemptionLimit,
      }),
    );
  }

  update(props: CouponPayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(COUPONS_BASE_V1, {
        id: props.id,
        code: props.code,
        name: props.name,
        description: props.description,
        programName: props.programName,
        companyName: props.companyName,
        resellerId: props.resellerId,
        categoryId: props.categoryId,
        orderRedemptionLimit: props.orderRedemptionLimit,
        ticketRedemptionLimit: props.ticketRedemptionLimit,
      }),
    );
  }

  remove(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${COUPONS_BASE_V1}/${id}`));
  }

  getEffectiveDates(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${COUPONS_BASE_V1}/${id}/dates`));
  }

  getCouponsByCategory(categoryId: number | string, props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${COUPONS_BASE_V2}/category/${categoryId}`, {
        params: buildParams(props, PAGINATION_SEARCH_FIELDS),
      }),
    );
  }

  readonly categories = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(CATEGORIES_BASE_V2, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),

    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${CATEGORIES_BASE_V1}/${id}`)),

    create: (props: { name: string; description?: string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(CATEGORIES_BASE_V1, {
          name: props.name,
          description: props.description,
        }),
      ),

    update: (props: { id: number | string; name: string; description?: string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(CATEGORIES_BASE_V1, {
          id: props.id,
          name: props.name,
          description: props.description,
        }),
      ),

    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${CATEGORIES_BASE_V1}/${id}`)),
  };

  readonly effectiveDates = {
    list: (): Promise<AjsResponse<unknown>> => wrapAjs(this.http.get<unknown>(EFFECTIVE_DATES_BASE_V1)),

    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${EFFECTIVE_DATES_BASE_V1}/${id}`)),

    create: (props: {
      couponId: number | string;
      effectiveStartDate: unknown;
      effectiveEndDate: unknown;
      notes?: string;
    }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(EFFECTIVE_DATES_BASE_V1, {
          couponId: props.couponId,
          effectiveStartDate: props.effectiveStartDate,
          effectiveEndDate: props.effectiveEndDate,
          notes: props.notes ?? '',
        }),
      ),

    update: (props: {
      id: number | string;
      couponId: number | string;
      effectiveStartDate: unknown;
      effectiveEndDate: unknown;
      notes?: string;
    }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(EFFECTIVE_DATES_BASE_V1, {
          id: props.id,
          couponId: props.couponId,
          effectiveStartDate: props.effectiveStartDate,
          effectiveEndDate: props.effectiveEndDate,
          notes: props.notes ?? '',
        }),
      ),

    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${EFFECTIVE_DATES_BASE_V1}/${id}`)),
  };

  readonly templates = {
    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${TEMPLATES_BASE_V1}/${id}`)),

    // Legacy comment: "Since all operations are post, put, or delete we are
    // opting to use POST instead of PUT". Preserved verbatim.
    update: (props: { eventTemplateId: number | string; data: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(`${TEMPLATES_BASE_V1}/${props.eventTemplateId}`, props.data)),
  };
}
